import { t, useLocale, errorMessage } from '../lib/i18n.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { LETTERS, type ClientMessage, type RoomSettings } from '@listen-draw/shared';
import { keys, playUrl, storage } from '../lib/api.js';
import { GameSocket } from '../lib/socket.js';
import { useStore } from '../lib/store.js';
import { Player as TtsPlayer, listEnglishVoices, ttsSupported } from '../lib/tts.js';
import { playRecording, SOUND_CHECK, unlockAudio } from '../lib/sound.js';
import { track } from '../lib/analytics.js';
import { qrDataUrl } from '../lib/qr.js';
import { useCountdownSeconds } from '../lib/time.js';
import { Canvas } from '../components/Canvas.js';
import { Choices } from '../components/Choices.js';
import { Explanation } from '../components/Explanation.js';
import { AudioSpeed } from '../components/AudioSpeed.js';
import { Ranking } from '../components/Ranking.js';
import { ConnStatus } from '../components/ConnStatus.js';
import { Podium } from '../components/Podium.js';

const PHASE_LABEL = (): Record<string, string> => ({
  lobby: t('Waiting', '待機中'),
  countdown_audio: t('Audio starts soon', 'まもなく音声'),
  listening: t('Listening', '音声再生中'),
  preparing: t('Preparing sketch', '線画を準備中'),
  countdown_draw: t('Drawing starts soon', 'まもなく描画'),
  drawing: t('Drawing', '描画中'),
  final: t('Last chance', '最終回答タイム'),
  reveal: t('Reveal', '答え合わせ'),
  results: t('Results', '結果発表'),
});

export function Host() {
  useLocale();
  const { code = '' } = useParams();
  const token = storage.get(keys.host(code));
  const sockRef = useRef<GameSocket | null>(null);
  const state = useStore((s) => s.state);
  const audio = useStore((s) => s.audio);
  const events = useStore((s) => s.events);
  const fatal = useStore((s) => s.fatal);
  const toast = useStore((s) => s.toast);
  const audioProgress = useStore((s) => s.audioProgress);
  const setAudioProgress = useStore((s) => s.setAudioProgress);
  const [qr, setQr] = useState<string>('');
  const [showTexts, setShowTexts] = useState(() => storage.get(keys.showTexts) === '1');
  const [voiceURI, setVoiceURI] = useState<string>(() => storage.get(keys.voice) ?? '');
  const [voices, setVoices] = useState(() => listEnglishVoices());
  const [soundStatus, setSoundStatus] = useState('');
  const soundCheckRef = useRef<AbortController | null>(null);
  useEffect(() => () => soundCheckRef.current?.abort(), []);
  const [log, setLog] = useState<string[]>([]);

  const send = useCallback(
    (m: ClientMessage) => {
      if (!sockRef.current?.send(m))
        toast(t('Not connected. Reconnecting…', 'サーバーに接続していません'), 'bad');
    },
    [toast],
  );

  // 接続
  useEffect(() => {
    if (!token || !code) return;
    const s = new GameSocket({ type: 'hello', role: 'host', roomCode: code, hostToken: token });
    s.connect();
    sockRef.current = s;
    return () => {
      s.close();
      sockRef.current = null;
      useStore.getState().reset();
    };
  }, [code, token]);

  useEffect(() => {
    qrDataUrl(playUrl(code))
      .then(setQr)
      .catch(() => setQr(''));
  }, [code]);

  useEffect(() => {
    if (!ttsSupported) return;
    const refresh = () => setVoices(listEnglishVoices());
    refresh();
    speechSynthesis.addEventListener('voiceschanged', refresh);
    return () => speechSynthesis.removeEventListener('voiceschanged', refresh);
  }, []);

  // 音声再生（Host が基準。audioTarget=players のときは発話せずサイレント進行）
  useEffect(() => {
    if (!audio || !state) return;
    const speak = state.settings.audioTarget !== 'players';
    const player = new TtsPlayer({
      items: audio.items,
      rate: audio.rate,
      gapMs: audio.gapMs,
      speak,
      voiceURI: voiceURI || null,
      onItemStart: (i) => {
        setAudioProgress(i);
        send({ type: 'audio:progress', letterIndex: i });
      },
      onError: () => {
        setSoundStatus(
          t(
            'Sound stopped. Check your volume and connection, then retry audio.',
            '音声が停止しました。音量と接続を確認して音声を再試行してください。',
          ),
        );
        track('audio_error', { mode: 'multiplayer' });
        send({ type: 'audio:failed' });
      },
      onDone: () => {
        setAudioProgress(null);
        send({ type: 'audio:done' });
      },
    });
    player.start();
    return () => {
      player.stop();
      setAudioProgress(null);
    };
    // voiceURI の変更で再生をやり直さない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audio?.id]);

  // イベント → ログ
  const lastEvent = events[0];
  useEffect(() => {
    if (!lastEvent) return;
    const line = describeEvent(lastEvent);
    if (line) setLog((l) => [line, ...l].slice(0, 30));
    if (lastEvent.type === 'round_aborted')
      toast(t('Restarting the round', 'ラウンドをやり直します'), 'bad');
  }, [lastEvent, toast]);

  const startSoundAction = async (message?: ClientMessage) => {
    soundCheckRef.current?.abort();
    const controller = new AbortController();
    soundCheckRef.current = controller;
    try {
      await unlockAudio();
      setSoundStatus('');
      if (message) {
        send(message);
      } else {
        await playRecording(SOUND_CHECK, state?.settings.ttsRate ?? 1, controller.signal);
        setSoundStatus(t('✓ Sound is ready.', '✓ 音声の準備ができました。'));
      }
    } catch {
      if (!controller.signal.aborted)
        setSoundStatus(
          t(
            'Sound could not play. Check your connection and try again.',
            '音声を再生できません。接続を確認して再試行してください。',
          ),
        );
    }
  };

  useEffect(() => {
    if (state && state.players.length >= 2 && state.roundsPlayed >= 1)
      track('host_activated', { mode: 'multiplayer' }, `${code}:${state.createdAt}`);
  }, [state, code]);

  const phase = state?.phase ?? 'lobby';
  const round = state?.round ?? null;
  const finalSecs = useCountdownSeconds(round?.final?.until, phase === 'final');
  const connectedCount = useMemo(
    () => state?.players.filter((p) => p.connected).length ?? 0,
    [state],
  );
  const answeredCount = round?.answeredCount ?? 0;
  const reveal = round?.reveal ?? null;
  const roundResult = useMemo(() => {
    if (!reveal || !state) return null;
    const byId = new Map(state.players.map((p) => [p.id, p]));
    const correct = reveal.outcomes
      .filter((o) => o.correct)
      .sort((a, b) => (a.rank ?? 99) - (b.rank ?? 99));
    const top = correct.slice(0, 3).map((o) => ({ ...o, name: byId.get(o.playerId)?.name ?? '?' }));
    const total = reveal.distribution.reduce((a, b) => a + b, 0);
    return { top, total, winner: top[0] ?? null };
  }, [reveal, state]);

  if (!token) {
    return (
      <main className="page page--narrow">
        <h1>{t('This browser does not own the room', 'Host 権限がありません')}</h1>
        <p className="muted">
          {t('Room ', 'このブラウザにはルーム ')}
          {code}{' '}
          {t(
            ' belongs to the browser that created it. Open that browser, or create a new room.',
            'の Host トークンが保存されていません。ルームを作成した端末で開くか、新しいルームを作成してください。',
          )}
        </p>
        <Link className="btn btn--primary" to="/">
          {t('Home', 'トップへ')}
        </Link>
      </main>
    );
  }
  if (fatal) {
    return (
      <main className="page page--narrow">
        <h1>{t('Could not connect', '接続できません')}</h1>
        <p className="error">{errorMessage(fatal.code)}</p>
        <Link className="btn btn--primary" to="/">
          {t('Home', 'トップへ')}
        </Link>
      </main>
    );
  }

  const settings = state?.settings;
  const inGame = phase !== 'lobby' && phase !== 'results';
  const revealText = round?.reveal ? round.reveal.texts[round.reveal.correctLetterIndex] : null;

  return (
    <main className="page">
      <header className="topbar">
        <h1>
          Listen &amp; Draw{' '}
          <small>
            {t('Host · Room ', 'Host · ルーム ')}
            {code}
          </small>
        </h1>
        <span className="spacer" />
        {state && (
          <span className="stat">
            {t('Round ', 'ラウンド ')}
            <b className="num">
              {round ? round.index : '–'} / {state.settings.rounds}
            </b>
          </span>
        )}
        <span className="stat">
          {t('Players ', '参加者 ')}
          <b className="num">{state?.players.length ?? 0}</b>
        </span>
        <ConnStatus />
      </header>

      {soundStatus && (
        <p className="note" role="status">
          {soundStatus}
        </p>
      )}
      {!state && <p className="muted">{t('Connecting…', '接続しています…')}</p>}

      {state && phase === 'lobby' && settings && (
        <div className="host-grid">
          <div className="stack">
            <section className="card">
              <p className="eyebrow">Join</p>
              <div className="join-box">
                {qr ? (
                  <img
                    src={qr}
                    alt={t(`Join QR code ${playUrl(code)}`, `参加用 QR コード ${playUrl(code)}`)}
                  />
                ) : (
                  <div style={{ width: 168, height: 168 }} />
                )}
                <div className="stack">
                  <div className="code-big num">{code}</div>
                  <div className="muted en small">{playUrl(code)}</div>
                  <button
                    className="btn"
                    onClick={() =>
                      void navigator.clipboard
                        .writeText(playUrl(code))
                        .then(() => toast(t('Invite link copied', '招待リンクをコピーしました')))
                        .catch(() =>
                          toast(
                            t(
                              'Copy the link shown above.',
                              '上のリンクを選択してコピーしてください。',
                            ),
                          ),
                        )
                    }
                  >
                    {t('Copy invite link', '招待リンクをコピー')}
                  </button>
                  <div className="muted small">
                    {settings.listed
                      ? t(
                          'Scan the QR code with your phone, or join from Open rooms on the home page.',
                          'スマホで QR を読み取るか、トップページの「いま開いている部屋」から誰でも参加できます。',
                        )
                      : t(
                          'This room is unlisted. Share the QR code or link with your players.',
                          '非公開ルームです。QR かコードを共有した人だけが参加できます。',
                        )}
                  </div>
                </div>
              </div>
            </section>
            <section className="card">
              <h2>{t('Settings', '設定')}</h2>
              <SettingsForm
                aiEnabled={state.aiEnabled}
                settings={settings}
                onChange={(patch) => send({ type: 'settings:update', patch })}
              />
              <div className="row" style={{ marginTop: 16 }}>
                <button className="btn" onClick={() => void startSoundAction()}>
                  {t('♫ Test sound', '♫ 音声をテスト')}
                </button>
                {state.aiEnabled && ttsSupported && (
                  <label>
                    {t('Voice for generated rounds', '生成問題の音声')}{' '}
                    <select
                      value={voiceURI}
                      onChange={(e) => {
                        setVoiceURI(e.target.value);
                        storage.set(keys.voice, e.target.value);
                      }}
                    >
                      <option value="">{t('Automatic', '自動')}</option>
                      {voices.map((v) => (
                        <option key={v.voiceURI} value={v.voiceURI}>
                          {v.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              <p className="note">
                {t(
                  'Recorded English audio is included. Test your speakers before starting.',
                  '英語の録音音声を同梱しています。開始前にスピーカーをテストしてください。',
                )}
              </p>
            </section>
          </div>
          <div className="stack">
            <section className="card">
              <h2>
                {t('Players ', '参加者 ')}
                {state.players.length}
              </h2>
              <Ranking
                players={state.players}
                onKick={(id) => send({ type: 'player:kick', playerId: id })}
              />
            </section>
            <button
              className="btn btn--primary btn--big"
              onClick={() => void startSoundAction({ type: 'game:start' })}
            >
              {t('Start game', 'ゲーム開始')}
              {state.players.length === 0
                ? t(' (preview without players)', '（参加者なしで試す）')
                : ''}
            </button>
            <p className="muted small">
              {state.aiEnabled
                ? t(
                    'New prompts and sketches are generated for each round.',
                    'AI がお題と線画を毎ラウンド生成します。',
                  )
                : t(
                    'Playing 100 practical, everyday listening questions with recorded audio.',
                    '録音音声付きの、現実的な日常場面のリスニング100問で遊べます。',
                  )}
            </p>
          </div>
        </div>
      )}

      {state && inGame && (
        <div className="host-grid">
          <div className="stack">
            <Canvas round={round} phase={phase} />
            {(phase === 'drawing' || phase === 'final') && (
              <div className="answered-line" aria-live="polite">
                <span>
                  {t('Answered ', '回答済み ')}
                  <b className="num">{answeredCount}</b> / {connectedCount}
                </span>
                <div className="answered-dots" aria-hidden="true">
                  {Array.from({ length: Math.min(connectedCount, 40) }).map((_, i) => (
                    <i key={i} className={i < answeredCount ? 'is-on' : ''} />
                  ))}
                </div>
              </div>
            )}
            <div className="odai">
              <span className="odai__label">{t('Sentence', 'お題')}</span>
              <span className={`odai__text en${revealText ? ' odai__text--shown' : ''}`}>
                {revealText ?? '「 ●●●●●●●●●●●●●● 」'}
              </span>
            </div>
            <div className="row">
              <span
                className={`pill${['listening', 'drawing', 'final'].includes(phase) ? ' pill--live' : phase === 'reveal' ? ' pill--ok' : ''}`}
              >
                {PHASE_LABEL()[phase]}
              </span>
              <span className="muted small">
                {hostMessage(phase, finalSecs, round?.audio.replays ?? 0, audioProgress)}
              </span>
            </div>
            <div className="row">
              {phase === 'listening' && (
                <button
                  className="btn"
                  onClick={() => void startSoundAction({ type: 'audio:retry' })}
                >
                  {t('Retry interrupted audio', '中断した音声を再試行')}
                </button>
              )}
              {(phase === 'listening' || phase === 'countdown_audio') && (
                <button className="btn" onClick={() => send({ type: 'audio:skip' })}>
                  {t('Skip audio', '音声をスキップ')}
                </button>
              )}
              {(phase === 'drawing' || phase === 'listening') && (
                <button
                  className="btn"
                  onClick={() => void startSoundAction({ type: 'audio:replay' })}
                  disabled={
                    phase === 'listening' &&
                    !round?.audio.done &&
                    (round?.audio.replays ?? 0) === 0 &&
                    !round?.audio.halved
                  }
                >
                  {t(
                    'Replay audio (everyone’s points are halved)',
                    'もう一度聞かせる（全員の得点半減） ',
                  )}
                </button>
              )}
              {phase === 'reveal' && (
                <button
                  className="btn btn--primary"
                  onClick={() => void startSoundAction({ type: 'round:next' })}
                >
                  {state.roundsPlayed >= state.settings.rounds
                    ? t('Show results', '結果発表へ')
                    : t('Next round', '次のラウンドへ')}
                </button>
              )}
              <button
                className="btn btn--ghost btn--danger"
                onClick={() =>
                  confirm(
                    t('End the game and show results?', 'ゲームを終了して結果発表に進みますか？'),
                  ) && send({ type: 'game:end' })
                }
              >
                {t('End game', 'ゲームを終了 ')}
              </button>
            </div>
            {reveal && roundResult && (
              <section className="card">
                <h3>{t('This round', 'このラウンド')}</h3>
                {roundResult.winner ? (
                  <p style={{ margin: 0 }}>
                    {t('Fastest correct: ', '最速正解: ')}
                    <b>{roundResult.winner.name}</b>
                    {t(' (at ', '（完成率 ')}
                    {roundResult.winner.pct}% · +{roundResult.winner.points}{' '}
                    {t(' points) ', '点） ')}
                  </p>
                ) : (
                  <p style={{ margin: 0 }}>{t('No correct answers', '正解者なし')}</p>
                )}
                {roundResult.top.length > 1 && (
                  <ol className="round-top">
                    {roundResult.top.map((o) => (
                      <li key={o.playerId}>
                        {o.name}{' '}
                        <span className="muted small">
                          {t('At ', '完成率 ')}
                          {o.pct}% · +{o.points}
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
                <div className="dist" aria-label={t('Answers by option', '選択肢ごとの回答数')}>
                  {LETTERS.map((L, i) => {
                    const n = reveal.distribution[i] ?? 0;
                    const w = roundResult.total ? Math.round((n / roundResult.total) * 100) : 0;
                    return (
                      <div
                        key={L}
                        className={`dist__row${i === reveal.correctLetterIndex ? ' dist__row--correct' : ''}`}
                      >
                        <span className="dist__letter">{L}</span>
                        <div className="dist__bar">
                          <i style={{ width: `${w}%` }} />
                        </div>
                        <span className="num dist__n">{n}</span>
                      </div>
                    );
                  })}
                </div>
                <p className="muted small" style={{ margin: '6px 0 0' }}>
                  {t('Answers ', '回答 ')}
                  {roundResult.total} / {state.players.length} {t(' players', '人 ')}
                </p>
              </section>
            )}
            {round?.reveal && (
              <section className="card">
                <h3>{t('The clue', '決め手')}</h3>
                <p style={{ margin: 0 }}>
                  {t('Answer: ', '正解は ')}
                  <b>{LETTERS[round.reveal.correctLetterIndex]}</b>
                  {t(' · Listen for: ', '。決め手の語:')}{' '}
                  {round.reveal.keyWords.map((w) => (
                    <span
                      key={w}
                      className="hint en"
                      style={{
                        marginRight: 6,
                        background: 'var(--hilite)',
                        padding: '0 6px',
                        borderRadius: 4,
                        fontWeight: 800,
                      }}
                    >
                      {w}
                    </span>
                  ))}
                </p>
                <p className="muted small" style={{ margin: '6px 0 0' }}>
                  {t('Differences: ', '差分軸: ')}
                  {round.reveal.axes.map((a) => `${a.name} (${a.values.join(' / ')})`).join('、')}
                  {round.drawingSource === 'ai'
                    ? t(' · AI-generated sketch', ' · 線画: AI 生成')
                    : t(' · Curated sketch', ' · 線画: 同梱')}
                </p>
              </section>
            )}
            {revealText && <Explanation text={revealText} />}
          </div>
          <div className="stack">
            <section className="card">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <h2 style={{ margin: 0 }}>{t('Options', '選択肢')}</h2>
                <label
                  className="small muted"
                  style={{ display: 'flex', gap: 6, alignItems: 'center' }}
                >
                  <input
                    type="checkbox"
                    checked={showTexts}
                    onChange={(e) => {
                      setShowTexts(e.target.checked);
                      storage.set(keys.showTexts, e.target.checked ? '1' : '0');
                    }}
                  />
                  {t(
                    'Show sentences (host only; keep off when projecting)',
                    '英文を表示（先生用・投影中は OFF） ',
                  )}
                </label>
              </div>
              <div style={{ height: 8 }} />
              <Choices
                round={round}
                playing={phase === 'listening' ? audioProgress : null}
                showTexts={showTexts}
                compact
              />
            </section>
            <section className="card">
              <h2>{t('Leaderboard', 'ランキング')}</h2>
              <Ranking players={state.players} limit={8} />
            </section>
            <section className="card">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <h2 style={{ margin: 0 }}>{t('Join', '参加')}</h2>
                <span className="num" style={{ fontWeight: 900, letterSpacing: '0.2em' }}>
                  {code}
                </span>
              </div>
              {qr && (
                <img
                  src={qr}
                  alt={t('Join QR code', '参加用 QR')}
                  style={{ width: 96, height: 96, marginTop: 8, borderRadius: 6 }}
                />
              )}
            </section>
            <div className="log">
              {log.map((l, i) => (
                <div key={i}>{l}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      {state && phase === 'results' && (
        <div className="stack" style={{ maxWidth: 720, margin: '0 auto', gap: 18 }}>
          <section className="card">
            <p className="eyebrow">Results</p>
            <Podium players={state.players} />
          </section>
          <section className="card">
            <h2>{t('All scores', '全員のスコア')}</h2>
            <Ranking players={state.players} />
          </section>
          <div className="row">
            <button className="btn btn--primary" onClick={() => send({ type: 'game:reset' })}>
              {t('Play again with this group', '同じメンバーでもう一度（ロビーへ）')}
            </button>
            <Link className="btn" to="/">
              {t('Home', 'トップへ')}
            </Link>
          </div>
        </div>
      )}
    </main>
  );
}

function hostMessage(
  phase: string,
  finalSecs: number,
  replays: number,
  progress: number | null,
): string {
  switch (phase) {
    case 'countdown_audio':
      return t(
        'The four English sentences play after the countdown.',
        'カウントダウンのあと A〜D の英文を再生します。',
      );
    case 'listening':
      return progress !== null
        ? t(
            `${LETTERS[progress]} is playing${replays ? ' (replay)' : ''}`,
            `${LETTERS[progress]} を読み上げ中${replays ? '（聞き直し）' : ''}`,
          )
        : t('Preparing audio…', '再生の準備中…');
    case 'preparing':
      return t(
        'Getting the next sketch ready. Please wait.',
        '線画を準備しています。少しお待ちください。',
      );
    case 'countdown_draw':
      return t('Get ready to choose!', '回答の準備！');
    case 'drawing':
      return t(
        'Watch the sketch. Tap A–D as soon as you know the answer.',
        '線画が描かれています。わかった瞬間に A〜D を押す！描画が終わったら答え合わせ。',
      );
    case 'final':
      return t(
        `The picture is complete. Answer within ${finalSecs} seconds for 50 points.`,
        `線画が完成。未回答の人は残り ${finalSecs} 秒以内に押せば 50 点。`,
      );
    case 'reveal':
      return t(
        'Review the sentences and the words that made the difference.',
        '英文を確認して聞き直しましょう。',
      );
    default:
      return '';
  }
}

function describeEvent(e: {
  type: string;
  playerName?: string;
  letterIndex?: number;
  points?: number;
  pct?: number;
  message?: string;
}): string | null {
  switch (e.type) {
    case 'answered':
      return t(
        `${e.playerName} answered at ${e.pct}%`,
        `${e.playerName} が回答（完成率 ${e.pct}%）`,
      );
    case 'joined':
      return t(`${e.playerName} joined`, `${e.playerName} が参加`);
    case 'left':
      return t(`${e.playerName} disconnected`, `${e.playerName} が切断`);
    case 'replay':
      return t('Audio replay (points halved)', '聞き直し（得点半減）');
    case 'round_aborted':
      return t('Round restarted', 'ラウンドをやり直し');
    default:
      return null;
  }
}

function SettingsForm({
  settings,
  onChange,
  aiEnabled,
}: {
  aiEnabled: boolean;
  settings: RoomSettings;
  onChange: (patch: Partial<RoomSettings>) => void;
}) {
  const num = (key: keyof RoomSettings, min: number, max: number, step = 1) => (
    <input
      type="number"
      min={min}
      max={max}
      step={step}
      value={settings[key] as number}
      onChange={(e) => {
        const v = Number(e.target.value);
        if (Number.isFinite(v) && v >= min && v <= max)
          onChange({ [key]: v } as Partial<RoomSettings>);
      }}
    />
  );
  return (
    <div className="settings">
      <label>
        {t('Rounds ', 'ラウンド数 ')}
        {num('rounds', 1, 30)}
      </label>
      <label>
        {t('Level ', 'レベル ')}
        <select
          disabled={!aiEnabled}
          value={settings.level}
          onChange={(e) => onChange({ level: Number(e.target.value) as 1 | 2 | 3 })}
        >
          <option value={1}>{t('1: Short sentences', '1: 短文')}</option>
          <option value={2}>{t('2: Short dialogue', '2: 短い対話')}</option>
          <option value={3}>{t('3: Longer dialogue', '3: 長めの対話')}</option>
        </select>
      </label>
      <label>
        {t('Drawing time (seconds) ', '描画時間（秒） ')}
        {num('drawSeconds', 8, 90)}
      </label>
      <label>
        {t('Last chance (seconds; 0 = off) ', '最終回答（秒、0 で無効） ')}
        {num('finalSeconds', 0, 30)}
      </label>
      <label>
        {t('Wrong-answer penalty ', '誤答の減点 ')}
        <select
          value={settings.wrongPenalty}
          onChange={(e) => onChange({ wrongPenalty: Number(e.target.value) })}
        >
          <option value={100}>
            {t('−100 (recommended)', '−100（推奨: 当てずっぽうが割に合わない）')}
          </option>
          <option value={50}>{t('−50', '−50（従来）')}</option>
        </select>
      </label>
      <label>
        {t('Countdown (seconds) ', 'カウントダウン（秒） ')}
        {num('countdownSeconds', 0, 5)}
      </label>
      <label>
        {t('Play audio on ', '音声を流す端末 ')}
        <select
          value={settings.audioTarget}
          onChange={(e) => onChange({ audioTarget: e.target.value as RoomSettings['audioTarget'] })}
        >
          <option value="host">{t('Host (in a classroom)', 'Host のみ（教室）')}</option>
          <option value="players">{t('Players (online)', '参加者のみ（オンライン）')}</option>
          <option value="both">{t('Both', '両方')}</option>
        </select>
      </label>
      <AudioSpeed
        value={settings.ttsRate}
        onChange={(ttsRate) => onChange({ ttsRate })}
      />
      <label>
        {t('Gap between sentences (ms) ', '文の間隔（ms） ')}
        {num('gapMs', 300, 5000, 100)}
      </label>
      <label className="check">
        <input
          type="checkbox"
          checked={settings.hint}
          onChange={(e) => onChange({ hint: e.target.checked })}
        />
        {t('Show one word as a hint ', 'ヒント（読み上げ時に 1 単語表示） ')}
      </label>
      <label className="check">
        <input
          type="checkbox"
          checked={settings.autoReveal}
          onChange={(e) => onChange({ autoReveal: e.target.checked })}
        />
        {t('Reveal when everyone has answered ', '全員が回答したら描画を待たずに答え合わせ ')}
      </label>
      <label className="check">
        <input
          type="checkbox"
          checked={settings.listed}
          onChange={(e) => onChange({ listed: e.target.checked })}
        />
        {t(
          'List this room publicly (anyone can join) ',
          'トップページの一覧に公開（誰でも参加できる） ',
        )}
      </label>
    </div>
  );
}
